/* ============================================================================
   ACEPLORE · module-product.js — product form, variant selection, media gallery,
   sticky add bar, quick view. Custom elements: <lumina-product-form>,
   <lumina-gallery>, <lumina-quickview>.
   ========================================================================== */
(function () {
  'use strict';
  const { utils, events, EVENTS, config } = window.Lumina;

  /* ---- Variant selection + add to cart ---------------------------------- */
  class ProductForm extends window.Lumina.Element {
    connectedCallback() {
      this.form = this.querySelector('form[data-product-form]') || this.querySelector('form');
      this.idInput = this.querySelector('[name="id"]');
      this.data = this.safeParse(this.querySelector('[data-variants-json]'));
      this.priceTarget = this.querySelector('[data-price-target]');
      this.variantStatus = this.querySelector('[data-variant-status]');
      this.submitBtn = this.querySelector('[data-add-button]');
      this.dynamicCheckout = this.querySelector('.shopify-payment-button');
      this.optionInputs = utils.qsa('[data-option-input]', this);

      this.optionInputs.forEach((i) => this.listen(i, 'change', () => this.onOptionChange()));
      if (this.form) this.listen(this.form, 'submit', (e) => this.onSubmit(e));
      this.onOptionChange(true);
    }
    safeParse(el) { try { return el ? JSON.parse(el.textContent) : []; } catch (_) { return []; } }
    selectedOptions() {
      const groups = {};
      this.optionInputs.forEach((i) => {
        if (i.type === 'radio' && !i.checked) return;
        groups[i.dataset.optionPosition] = i.value;
      });
      return Object.keys(groups).sort().map((k) => groups[k]);
    }
    findVariant(opts) {
      return this.data.find((v) => v.options.every((o, idx) => o === opts[idx]));
    }
    onOptionChange(initial) {
      const opts = this.selectedOptions();
      const variant = this.findVariant(opts) || (initial ? this.data.find((v) => v.available) : null);
      this.current = variant;
      if (!variant) {
        this.setUnavailable();
        if (this.variantStatus) this.variantStatus.textContent = config.strings.soldOut || 'Unavailable';
        events.emit('variant:change', { variant: null, sectionId: this.dataset.section });
        return;
      }
      if (this.idInput) { this.idInput.disabled = false; this.idInput.value = variant.id; }
      // Update URL (without reload) for shareable variant links — but never
      // from inside the quick-view modal, which lives on someone else's page.
      if (!initial && variant.id && !this.closest('lumina-quickview')) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
      this.updatePrice(variant);
      this.updateButton(variant);
      this.syncDynamicCheckout(variant.available);
      this.updateMedia(variant);
      if (this.variantStatus) {
        const state = variant.available ? config.strings.addToCart : config.strings.soldOut;
        this.variantStatus.textContent = `${opts.join(', ')}. ${utils.formatMoney(variant.price)}. ${state}`;
      }
      events.emit('variant:change', { variant, sectionId: this.dataset.section });
    }
    updatePrice(variant) {
      if (!this.priceTarget) return;
      let html = `<span class="lm-price__amount${variant.compare_at_price > variant.price ? ' lm-price__amount--sale' : ''}">${utils.formatMoney(variant.price)}</span>`;
      if (variant.compare_at_price > variant.price) {
        html += ` <s class="lm-price__compare">${utils.formatMoney(variant.compare_at_price)}</s>`;
        const pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
        html += ` <span class="lm-badge lm-badge--sale">-${pct}%</span>`;
      }
      this.priceTarget.innerHTML = html;
    }
    updateButton(variant) {
      if (!this.submitBtn) return;
      const label = this.submitBtn.querySelector('[data-add-label]') || this.submitBtn;
      if (variant.available) { this.submitBtn.disabled = false; label.textContent = config.strings.addToCart; }
      else { this.submitBtn.disabled = true; label.textContent = config.strings.soldOut; }
    }
    syncDynamicCheckout(available) {
      if (!this.dynamicCheckout) return;
      this.dynamicCheckout.hidden = !available;
      this.dynamicCheckout.toggleAttribute('inert', !available);
      this.dynamicCheckout.setAttribute('aria-hidden', String(!available));
    }
    setUnavailable() {
      if (this.submitBtn) { this.submitBtn.disabled = true; const l = this.submitBtn.querySelector('[data-add-label]') || this.submitBtn; l.textContent = config.strings.soldOut || 'Unavailable'; }
      if (this.idInput) { this.idInput.value = ''; this.idInput.disabled = true; }
      this.syncDynamicCheckout(false);
    }
    updateMedia(variant) {
      if (!variant.featured_media) return;
      const show = () => {
        if (!this.current || this.current.id !== variant.id) return;
        const gallery = this.closest('section')?.querySelector('lumina-gallery') || document.querySelector('lumina-gallery');
        if (gallery?.showMedia) gallery.showMedia(variant.featured_media.id);
      };
      const gallery = this.closest('section')?.querySelector('lumina-gallery') || document.querySelector('lumina-gallery');
      if (gallery?.showMedia) show();
      else if (window.customElements?.whenDefined) customElements.whenDefined('lumina-gallery').then(show);
    }
    async onSubmit(e) {
      e.preventDefault();
      if (!this.current || !this.current.available) return;
      const btn = this.submitBtn;
      const label = btn.querySelector('[data-add-label]') || btn;
      btn.disabled = true; btn.classList.add('is-loading');
      label.textContent = '';
      const spinner = document.createElement('span'); spinner.className = 'lm-spinner';
      btn.prepend(spinner);
      try {
        const fd = new FormData(this.form);
        const items = { id: fd.get('id'), quantity: parseInt(fd.get('quantity') || '1', 10) };
        const props = {};
        for (const [k, v] of fd.entries()) { if (k.startsWith('properties[')) props[k.slice(11, -1)] = v; }
        if (Object.keys(props).length) items.properties = props;
        const sp = fd.get('selling_plan'); if (sp) items.selling_plan = sp;
        await window.Lumina.addToCart(items);
        label.textContent = config.strings.added || 'Added';
        spinner.remove();
        setTimeout(() => {
          btn.classList.remove('is-loading');
          if (this.current) this.updateButton(this.current);
          else this.setUnavailable();
        }, 1400);
      } catch (err) {
        spinner.remove();
        btn.classList.remove('is-loading');
        if (this.current) this.updateButton(this.current);
        else this.setUnavailable();
        Lumina.toast(err.message || config.strings.cartError, { type: 'error' });
      }
    }
  }
  if (!customElements.get('lumina-product-form')) customElements.define('lumina-product-form', ProductForm);

  /* ---- Media gallery ---------------------------------------------------- */
  class Gallery extends window.Lumina.Element {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.thumbs = utils.qsa('[data-gallery-thumb]', this);
      this.thumbs.forEach((t) => this.listen(t, 'click', () => this.showMedia(t.dataset.mediaId)));
      this.listen(this, 'keydown', (e) => {
        if (e.key === 'ArrowRight') this.step(1);
        if (e.key === 'ArrowLeft') this.step(-1);
      });
    }
    showMedia(id) {
      const slides = utils.qsa('[data-media-id]', this.main);
      let idx = slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (idx < 0) return;
      this.current = idx;
      slides.forEach((s, i) => {
        const isActive = i === idx;
        s.classList.toggle('is-active', isActive);
        s.setAttribute('aria-hidden', String(!isActive));
        s.toggleAttribute('inert', !isActive);
        if (!isActive) s.querySelectorAll('video, audio').forEach((media) => media.pause());
      });
      const active = slides[idx];
      if (active && active.scrollIntoView) active.scrollIntoView({ behavior: utils.prefersReducedMotion() ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
      // 'true', not 'page' — these buttons aren't navigation links.
      this.thumbs.forEach((t) => t.setAttribute('aria-current', t.dataset.mediaId === String(id) ? 'true' : 'false'));
    }
    step(dir) {
      const slides = utils.qsa('[data-media-id]', this.main);
      if (!slides.length) return;
      const next = ((this.current || 0) + dir + slides.length) % slides.length;
      this.showMedia(slides[next].dataset.mediaId);
    }
  }
  if (!customElements.get('lumina-gallery')) customElements.define('lumina-gallery', Gallery);

  /* ---- Sticky add-to-cart bar ------------------------------------------- */
  class StickyAdd extends window.Lumina.Element {
    connectedCallback() {
      const anchor = document.querySelector(this.dataset.watch || '[data-add-button]');
      if (!anchor || !('IntersectionObserver' in window)) return;
      this.priceEl = this.querySelector('.lm-sticky-add__price');
      this.btn = this.querySelector('[data-sticky-add]');
      this.labelEl = this.querySelector('[data-sticky-label]');
      this.sub('variant:change', (detail) => {
        if (this.dataset.section && detail?.sectionId !== this.dataset.section) return;
        const variant = detail && detail.variant;
        if (variant && this.priceEl) this.priceEl.textContent = utils.formatMoney(variant.price || 0);
        if (this.btn) this.btn.disabled = !variant || !variant.available;
        if (this.labelEl) this.labelEl.textContent = variant && variant.available
          ? config.strings.addToCart
          : (config.strings.soldOut || 'Unavailable');
      });
      this._pastAnchor = false;
      this._atFooter = false;
      const apply = () => this.classList.toggle('is-visible', this._pastAnchor && !this._atFooter);
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { this._pastAnchor = !e.isIntersecting && e.boundingClientRect.top < 0; });
        apply();
      }, { threshold: 0 });
      io.observe(anchor);
      this._cleanups.push(() => io.disconnect());
      // Hide the fixed bar once the footer enters the viewport so it never
      // overlaps the footer content at the bottom of the page.
      const footer = document.querySelector('.lm-footer');
      if (footer) {
        const fio = new IntersectionObserver((entries) => {
          entries.forEach((e) => { this._atFooter = e.isIntersecting; });
          apply();
        }, { threshold: 0 });
        fio.observe(footer);
        this._cleanups.push(() => fio.disconnect());
      }
      if (this.btn) this.listen(this.btn, 'click', () => {
        const realBtn = document.querySelector('[data-add-button]');
        realBtn && realBtn.click();
      });
    }
  }
  if (!customElements.get('lumina-sticky-add')) customElements.define('lumina-sticky-add', StickyAdd);

  /* ---- Read-more (collapses long descriptions) -------------------------- */
  class ReadMore extends window.Lumina.Element {
    connectedCallback() {
      this.content = this.querySelector('[data-readmore-content]');
      this.toggle = this.querySelector('[data-readmore-toggle]');
      this.label = this.querySelector('[data-readmore-label]');
      if (!this.content || !this.toggle) return;
      const h = parseInt(this.dataset.collapsedHeight, 10) || 320;
      this.moreLabel = this.dataset.moreLabel || 'Read more';
      this.lessLabel = this.dataset.lessLabel || 'Show less';
      this.style.setProperty('--lm-rm-h', h + 'px');
      // Only collapse when there's enough overflow to be worth a toggle.
      const check = () => {
        if (this.content.scrollHeight > h + 80) {
          this.setAttribute('data-collapsed', '');
          this.toggle.hidden = false;
        }
      };
      if ('requestAnimationFrame' in window) requestAnimationFrame(check); else check();
      this.listen(this.toggle, 'click', () => {
        const collapsed = this.toggleAttribute('data-collapsed');
        this.toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        if (this.label) this.label.textContent = collapsed ? this.moreLabel : this.lessLabel;
        if (collapsed) this.scrollIntoView({ behavior: utils.prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
      });
    }
  }
  if (!customElements.get('lumina-readmore')) customElements.define('lumina-readmore', ReadMore);

  /* ---- Quick view ------------------------------------------------------- */
  class QuickView extends window.Lumina.Element {
    connectedCallback() {
      this.overlay = this.querySelector('[data-quickview-overlay]');
      this.panel = this.querySelector('[data-quickview-panel]');
      this.content = this.querySelector('[data-quickview-content]');
      this.release = null;
      this.listen(document, 'click', (e) => {
        const trigger = e.target.closest('[data-quickview-trigger]');
        if (trigger) { e.preventDefault(); this.open(trigger.dataset.productHandle); }
      });
      this.querySelectorAll('[data-quickview-close]').forEach((b) => this.listen(b, 'click', () => this.close()));
      this.listen(this.overlay, 'click', () => this.close());
      this.listen(document, 'keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); });
      this.sub(EVENTS.CART_ADD, () => { if (this.isOpen) this.close(); });
    }
    async open(handle) {
      if (!handle || this.isOpen) return;
      if (this._request) this._request.abort();
      const request = (this._request = new AbortController());
      this.isOpen = true;
      utils.lockScroll(true);
      this.overlay.setAttribute('aria-hidden', 'false');
      this.panel.removeAttribute('inert');
      this.panel.setAttribute('aria-hidden', 'false');
      this.content.innerHTML = '<div class="lm-quickview__loading"><span class="lm-spinner"></span></div>';
      this.release = utils.trapFocus(this.panel, document.activeElement);
      try {
        // routes.root is '/fr' (no trailing slash) on secondary locales.
        const root = config.routes.root.endsWith('/') ? config.routes.root : config.routes.root + '/';
        const res = await fetch(`${root}products/${handle}?section_id=quick-view`, { signal: request.signal });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const html = await res.text();
        if (!this.isOpen || this._request !== request) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('[data-quickview-inner]');
        if (!inner) throw new Error('quick-view section missing');
        this.content.innerHTML = inner.innerHTML;
        // Upgrade any custom elements inside.
        events.emit('section:load', { target: this.content });
      } catch (err) {
        if (err?.name === 'AbortError' || !this.isOpen || this._request !== request) return;
        this.content.innerHTML = `<p class="lm-quickview__error">${utils.escapeHTML(config.strings.cartError || 'Unable to load')}</p>`;
      } finally {
        if (this._request === request) this._request = null;
      }
    }
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      if (this._request) { this._request.abort(); this._request = null; }
      utils.lockScroll(false);
      this.release && this.release();
      this.release = null;
      this.overlay.setAttribute('aria-hidden', 'true');
      this.panel.setAttribute('aria-hidden', 'true');
      this.panel.setAttribute('inert', '');
    }
  }
  if (!customElements.get('lumina-quickview')) customElements.define('lumina-quickview', QuickView);

  /* ---- Recently viewed (records product handle from PDP) ---------------- */
  (function recentlyViewed() {
    const root = document.querySelector('[data-product-root]');
    if (!root || !root.dataset.productHandle) return;
    const KEY = 'lumina:recent';
    let list = utils.storage.get(KEY, []);
    const h = root.dataset.productHandle;
    list = [h, ...list.filter((x) => x !== h)].slice(0, 12);
    utils.storage.set(KEY, list);
  })();
})();
